import { inject } from "@angular/core";
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from "@angular/common/http";
import { Observable, ReplaySubject, throwError } from "rxjs";
import {
  catchError,
  exhaustMap,
  filter,
  switchMap,
  take,
} from "rxjs/operators";
import { AuthService } from "./auth.service";
import { CLIENT_API_CONTRACT_VERSION } from "../shared/common.enums";
import { AppVersionService } from "../shared/app-version.service";
import { environment } from "src/environments/environment";

const AUTH_ROUTES = ["/auth/login", "/auth/refresh", "/auth/logout"];
const GUEST_ROUTES = ["/api/guest/"];
const REFRESH_TOKEN_ERROR = "Refresh failed: No token in response";
const VERSION_UPDATE_ERROR = "Actualizando versión de la aplicación...";

let isRefreshing = false;
// Each refresh attempt gets its own subject. A failed refresh must notify every
// request waiting behind it; leaving those requests subscribed to a subject that
// only emits `null` makes the application remain in its boot/loading state.
let refreshTokenSubject = new ReplaySubject<string>(1);

function addToken(request: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
  return request.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
}

function addVersionHeader(request: HttpRequest<unknown>): HttpRequest<unknown> {
  return request.clone({
    setHeaders: { "X-Client-Version": CLIENT_API_CONTRACT_VERSION },
  });
}

function handleContractError(error: unknown, appVersionService: AppVersionService): Observable<never> {
  if (
    error instanceof HttpErrorResponse &&
    error.status === 500 &&
    error.error?.code === "CLIENT_API_CONTRACT_UNSUPPORTED"
  ) {
    appVersionService.notifyClientContractUnsupported(error.error?.msg);
  }
  return throwError(() => error);
}

function handle401Error(
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
  authService: AuthService,
  appVersionService: AppVersionService,
): Observable<HttpEvent<unknown>> {
  if (appVersionService.isVersionUpdateInProgress()) {
    return throwError(() => VERSION_UPDATE_ERROR);
  }

  if (!isRefreshing) {
    isRefreshing = true;
    refreshTokenSubject = new ReplaySubject<string>(1);

    return authService.refreshToken().pipe(
      switchMap((res: { accessToken?: string }) => {
        isRefreshing = false;
        const newToken = res?.accessToken;
        if (!newToken) {
          return throwError(() => REFRESH_TOKEN_ERROR);
        }
        authService.updateToken(newToken);
        refreshTokenSubject.next(newToken);
        refreshTokenSubject.complete();
        return next(addToken(request, newToken));
      }),
      catchError((err) => {
        isRefreshing = false;
        // Wake all requests queued while the refresh was in progress. Without
        // this error signal, their `take(1)` subscriptions never finish.
        refreshTokenSubject.error(err);
        if (!appVersionService.isVersionUpdateInProgress()) {
          void authService.logout();
        }
        return throwError(() => err);
      }),
    );
  }

  return refreshTokenSubject.pipe(
    filter((token): token is string => token != null),
    take(1),
    switchMap((token) => next(addToken(request, token))),
  );
}

export const authInterceptorFn: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const appVersionService = inject(AppVersionService);

  const isExternal = req.url.startsWith("http") &&
    !req.url.startsWith(environment.endpoint) &&
    !req.url.startsWith(environment.phpEndpoint);

  if (isExternal) {
    return next(req);
  }

  const isGuestRoute = GUEST_ROUTES.some((route) => req.url.includes(route));
  if (isGuestRoute) {
    return next(req);
  }

  const isAuthRoute = AUTH_ROUTES.some((route) => req.url.includes(route));

  if (isAuthRoute) {
    return next(addVersionHeader(req)).pipe(
      catchError((error) => handleContractError(error, appVersionService)),
    );
  }

  return authService.user.pipe(
    take(1),
    exhaustMap((user) => {
      let modifiedReq = req;
      const accessToken = user?.token;

      if (accessToken) {
        modifiedReq = addToken(modifiedReq, accessToken);
      }

      modifiedReq = addVersionHeader(modifiedReq);

      return next(modifiedReq).pipe(
        catchError((error) => {
          if (error instanceof HttpErrorResponse) {
            if (error.status === 500 && error.error?.code === "CLIENT_API_CONTRACT_UNSUPPORTED") {
              return handleContractError(error, appVersionService);
            }
            if (error.status === 401) {
              return handle401Error(modifiedReq, next, authService, appVersionService);
            }
          }
          return throwError(() => error);
        }),
      );
    }),
  );
};
